// Functions
import {plainObjectToDF} from '../helpers/shared';

// Classes
import {DisplayField} from '../DisplayField';
import {calculateExpirationDate} from './comps/lease';

/**
 * @typedef {import('@/lib/classes/InfoWindowContent/aggregators/shared.js').Aggregator} Aggregator
 */

/**
 * Given a list of blob comps, returns the total premise area
 * @param {Array<Object>} comps an array of comps
 * @return {number} the total premise area
 */
function getTotalPremiseArea(comps) {
  let totalPremiseArea = 0;
  comps
    .filter((comp) => comp.group === 'Lease' || comp.type === 'Lease')
    .forEach((comp) => {
      // If the comp is expired, return early
      const term = comp.displayFields?.find((field) => field.label === 'Term')?.value;
      const startDate = comp.displayFields?.find((field) => field.label === 'Start Date')?.value;
      const expirationDate = calculateExpirationDate(term, startDate);
      if (expirationDate && (new Date(expirationDate) < new Date())) return;

      let premiseArea = comp.displayFields?.find((field) => field.label === 'Premise Area')?.value || null;
      // If the premise area does not exist, return
      if (['', undefined, null].includes(premiseArea)) return;

      // If the comp's premise area is a string replace the commas
      if (typeof premiseArea === 'string') {
        premiseArea = premiseArea.replace(/,/g, '');
      }

      totalPremiseArea += parseFloat(premiseArea);
    });

  return totalPremiseArea;
}

/**
 * @type {Aggregator}
 */
const address = {
  function: function(blob) {
  // If the aggregated address field exists, return it
    const addressDisplayField = blob.displayFields?.find((df) => df.label === 'Address' && df.unit === 'address');
    if (addressDisplayField) return [plainObjectToDF(addressDisplayField)];

    // Fields specific to addresses we want from the blob
    const addressFields = [
      'Address',
      'City',
      'State',
    ];

    // Get display fields for address
    const displayFields = addressFields
      .map((key) => { // map to display fields
        return new DisplayField({
          label: key,
          unit: 'string',
          value: blob.displayFields?.find((df) => df.label === key && !df.displayFields?.length) || undefined,
          allowEdit: true,
          allowLabelEdit: false,
          allowDelete: false,
        });
      });

    // The address display field
    return [new DisplayField({
      label: 'Address',
      unit: 'address',
      displayFields,
      allowEdit: true,
    })];
  },
  name: 'address',
};

/**
 * @type {Aggregator}
 */
const lastModifiedBy = {
  /**
   * Given a blob, creates the lastModifiedBy displayField
   * @param {Object} blob the incoming competitive building metadata
   * @return {[DisplayField]} [displayField]
   * displayField is the display field for last modified by
   */
  function: function(blob) {
    if (!blob.editedOn?.timestamp) return [];
    // Add a `Last Modified By` field to the displayFields if the content is a competitive building
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const {editedOn} = blob;
    let output = `${editedOn.name},\n`;
    // Breaking out each segment of the date is necessary here for our desired format while accounting for
    // current user's timezone
    if (editedOn?.timestamp) {
      const lastEditedDate = editedOn.timestamp;
      const date = lastEditedDate.seconds ? new Date(lastEditedDate.seconds * 1000) : new Date(lastEditedDate);
      const year = date.toLocaleDateString('en-US', {year: 'numeric'});
      const month = date.toLocaleDateString('en-US', {month: '2-digit'});
      const day = date.toLocaleDateString('en-US', {day: 'numeric'});
      const time = date.toLocaleTimeString('en-US', {timeZone: timezone, hour: 'numeric', minute: 'numeric'});
      output += `${year}-${month}-${day} ${time}`;
    }

    return [
      new DisplayField({
        label: 'Last Modified By',
        unit: 'string',
        value: output,
        allowEdit: false,
      }),
      () => {},
    ];
  },
  name: 'lastModifiedBy',
};

/**
 * @type {Aggregator}
 */
const totalAvailable = {
  /**
   * Total Available is a calculation for buildings of the 'building size' - 'premise area'
   * @param {Object} blob - the incoming blob data to extract relevant fields from
   * @return {Array<DisplayField>} [totalAvailableDf]
   * totalAvailableDf is a displayField formatted properly
   */
  function: function(blob) {
    let displayField;
    let buildingSize = blob.displayFields?.find((field) => field.label === 'Building Size')?.value || null;
    const buildingStage = blob.displayFields?.find((field) => field.label === 'Building Stage')?.value || null;

    const overrideValue = buildingStage === 'Fully Leased';

    // if building size is a string, remove commas
    if (typeof buildingSize === 'string') {
      buildingSize = buildingSize.replace(/,/g, '');
    }

    if ((buildingSize && !isNaN(buildingSize)) || overrideValue) {
      const value = overrideValue ? 0 : Math.max(buildingSize - getTotalPremiseArea(blob.comps), 0);
      displayField = new DisplayField({
        label: 'Total Available',
        value: value,
        unit: 'squareFeet',
        allowEdit: false,
        valueOverridden: overrideValue,
      });
    }

    if (!buildingSize && !overrideValue) {
      // Communicate that the field should be removed by overriding the value to null
      displayField = new DisplayField({
        label: 'Total Available',
        value: null,
        unit: 'squareFeet',
        allowEdit: false,
        valueOverridden: true,
      });
    }

    // Return a new display field with the value
    return [displayField];
  },
  name: 'totalAvailable',
};

/**
 * @type {Aggregator}
 */
const leasedPercentage = {
  /**
   * Leased Percentage is a calculation for buildings of the 'premise area' / 'building size' * 100
   * @param {Object} blob - the incoming blob data to extract relevant fields from
   * @return {Array<DisplayField>} [leasedPercentageDf]
   * totalAvailableDf is a displayField formatted properly
   */
  function: function(blob) {
    let displayField;
    let buildingSize = blob.displayFields?.find((field) => field.label === 'Building Size')?.value || null;
    const buildingStage = blob.displayFields?.find((field) => field.label === 'Building Stage')?.value || null;

    const overrideValue = buildingStage === 'Fully Leased';

    // if building size is a string, remove commas
    if (typeof buildingSize === 'string') {
      buildingSize = buildingSize.replace(/,/g, '');
    }

    if ((buildingSize && !isNaN(buildingSize)) || overrideValue) {
      const value = overrideValue ? 100 : ((getTotalPremiseArea(blob.comps) / buildingSize) * 100).toFixed(2);
      displayField = new DisplayField({
        label: 'Leased Percentage',
        value: value,
        unit: 'percentage',
        allowEdit: false,
        valueOverridden: overrideValue,
      });
    }

    if (!buildingSize && !overrideValue) {
      // Communicate that the field should be removed by overriding the value to null
      displayField = new DisplayField({
        label: 'Leased Percentage',
        value: null,
        unit: 'percentage',
        allowEdit: false,
        valueOverridden: true,
      });
    }

    return [displayField];
  },
  name: 'leasedPercentage',
};

export const aggregators = [
  address,
  lastModifiedBy,
  totalAvailable,
  leasedPercentage,
];
